// Pure tz-aware formatting helpers — no React imports here so this module
// is safe to execute under Node for smoke / CI checks.
//
// See `/app/frontend/src/lib/format.js` for the React hook surface
// (`useTenantTz()`, `useFormatters()`) layered on top of this module.

export const FALLBACK_TIMEZONE = "Asia/Kolkata";

export function formatMoney(amountMinor, currency = "INR", locale = "en-IN") {
    if (amountMinor == null) return "—";
    const major = Number(amountMinor) / 100;
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
        }).format(major);
    } catch {
        return `${currency} ${major.toFixed(2)}`;
    }
}

function _resolveTz(opts) {
    return (opts && opts.timeZone) || FALLBACK_TIMEZONE;
}
function _resolveLocale(opts) {
    return (opts && opts.locale) || "en-IN";
}

export function formatDate(value, opts = {}) {
    if (!value) return "—";
    try {
        const d = typeof value === "string" ? new Date(value) : value;
        return new Intl.DateTimeFormat(_resolveLocale(opts), {
            year: "numeric",
            month: "short",
            day: "2-digit",
            timeZone: _resolveTz(opts),
            ...opts.options,
        }).format(d);
    } catch {
        return String(value);
    }
}

export function formatDateTime(value, opts = {}) {
    if (!value) return "—";
    try {
        const d = typeof value === "string" ? new Date(value) : value;
        return new Intl.DateTimeFormat(_resolveLocale(opts), {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: _resolveTz(opts),
            ...opts.options,
        }).format(d);
    } catch {
        return String(value);
    }
}

export function formatTime(value, opts = {}) {
    if (!value) return "—";
    try {
        const d = typeof value === "string" ? new Date(value) : value;
        return new Intl.DateTimeFormat(_resolveLocale(opts), {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: _resolveTz(opts),
            ...opts.options,
        }).format(d);
    } catch {
        return String(value);
    }
}

// Relative time is tz-agnostic (millisecond diff). Falls back to formatDateTime
// (which IS tz-aware) only when the gap exceeds 7 days.
export function formatRelativeTime(value, opts = {}) {
    if (!value) return "—";
    const ms = Date.now() - new Date(value).getTime();
    const sec = Math.round(ms / 1000);
    if (Math.abs(sec) < 60) return sec >= 0 ? "just now" : "in a moment";
    const min = Math.round(sec / 60);
    if (Math.abs(min) < 60) return min >= 0 ? `${min}m ago` : `in ${-min}m`;
    const hr = Math.round(min / 60);
    if (Math.abs(hr) < 24) return hr >= 0 ? `${hr}h ago` : `in ${-hr}h`;
    const day = Math.round(hr / 24);
    if (Math.abs(day) < 7) return day >= 0 ? `${day}d ago` : `in ${-day}d`;
    return formatDateTime(value, opts);
}

export function formatDuration(seconds) {
    if (seconds == null) return "—";
    const s = Math.max(0, Math.floor(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
}

export function localeForLang(lang) {
    return lang === "hi" ? "hi-IN" : "en-IN";
}

// Wall-clock parts of an ISO instant in a given timezone — the only browser-
// native way to read individual fields in a specific tz without re-anchoring
// to the OS clock.
export function getTzParts(value, timeZone = FALLBACK_TIMEZONE) {
    if (!value) return null;
    const d = typeof value === "string" ? new Date(value) : value;
    const parts = new Intl.DateTimeFormat("en-GB", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false, timeZone,
    }).formatToParts(d).reduce((acc, p) => {
        if (p.type !== "literal") acc[p.type] = Number(p.value);
        return acc;
    }, {});
    if (parts.hour === 24) parts.hour = 0;
    return parts;
}

export function tzDayKey(value, timeZone = FALLBACK_TIMEZONE) {
    const p = getTzParts(value, timeZone);
    if (!p) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function tzMinutesIntoDay(value, dayKey, timeZone = FALLBACK_TIMEZONE) {
    const p = getTzParts(value, timeZone);
    if (!p) return null;
    const pad = (n) => String(n).padStart(2, "0");
    const valueDayKey = `${p.year}-${pad(p.month)}-${pad(p.day)}`;
    if (valueDayKey !== dayKey) return null;
    return p.hour * 60 + p.minute;
}

export function tzWallClockToUtc(year, month, day, hour, minute, timeZone = FALLBACK_TIMEZONE) {
    const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
    const p = getTzParts(new Date(naive), timeZone);
    const desiredSecs = Date.UTC(year, month - 1, day, hour, minute, 0) / 1000;
    const tzSecs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second || 0) / 1000;
    return new Date(naive - (tzSecs - desiredSecs) * 1000);
}
